import React from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@tanstack/react-query';

import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {appApiUrl} from 'common/report/queries/utils';
import {getProjectId} from 'common/report/util';
import {apiClient} from 'common/util/api-client';

import {
  AiSummaryResponse,
  KeyAction,
  RecordingEventMetadata,
  RecordingMetadata,
  RecordingPageViewEventMetadata,
  TimeDisplaySetting,
} from '../types';
import {formatRecordingProgressTime} from '../util';
import styles from './index.module.styl';

async function fetchAISummaryForEvents(
  events: RecordingEventMetadata[],
  replayPageViewEvents?: RecordingPageViewEventMetadata[],
): Promise<AiSummaryResponse | null> {
  try {
    const formattedEvents = events.map((event) => ({
      event_name: event.eventName,
      properties: Object.fromEntries(event.propertyMapping),
      timestamp: event.eventTime * 1000,
    }));

    if (replayPageViewEvents) {
      for (const pageViewEvent of replayPageViewEvents) {
        formattedEvents.push({
          event_name: `Page View`,
          properties: {url: pageViewEvent.currentURL},
          timestamp: pageViewEvent.eventTime * 1000,
        });
      }
    }

    formattedEvents.sort((a, b) => a.timestamp - b.timestamp);

    const url = appApiUrl(`ai/summarize-user-activity`, {projectId: getProjectId()});
    const response = await apiClient.post<AiSummaryResponse>(url, {
      events: formattedEvents,
      context: `User session replay events for analysis`,
    });

    return response;
  } catch (error) {
    console.error(`Failed to fetch AI summary:`, error);
    return null;
  }
}

export function AiSummaryPanel({
  replay,
  replayEvents,
  isEventsLoading,
  onKeyActionTimestampClick,
  replayPageViewEvents,
  onClose,
}: {
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  isEventsLoading: boolean;
  onKeyActionTimestampClick: (action: KeyAction) => void;
  replayPageViewEvents?: RecordingPageViewEventMetadata[];
  onClose?: () => void;
}) {
  const {t} = useTranslation();

  const hasEvents = !isEventsLoading && replayEvents.length > 0;
  const {
    data: aiSummary,
    isLoading: isAiSummaryLoading,
    error,
  } = useQuery({
    queryKey: [`ai-summary`, replay?.replayId],
    queryFn: () => fetchAISummaryForEvents(replayEvents, replayPageViewEvents),
    enabled: replay && hasEvents,
    staleTime: Infinity, // don't ever refetch
  });

  const isLoading = isEventsLoading || isAiSummaryLoading;

  let content;
  if (isLoading || !aiSummary) {
    content = (
      <div className={styles.aiSummaryLoading}>
        {...new Array(10).fill(null).map((_, index) => <div key={index} className='mp-skeleton-large' />)}
      </div>
    );
  } else if (error) {
    content = (
      <div className={styles.aiSummaryMain}>
        <p className={styles.aiSummaryText}>{t(`session-replay.ai-summary-no-recording-selected`)}</p>
        <p className={styles.aiSummaryText}>{error.message}</p>
      </div>
    );
  } else if (!replay) {
    content = (
      <div className={styles.aiSummaryMain}>
        <p className={styles.aiSummaryText}>{t(`session-replay.ai-summary-no-recording-selected`)}</p>
      </div>
    );
  } else if (!hasEvents) {
    content = (
      <div className={styles.aiSummaryMain}>
        <p className={styles.aiSummaryText}>{t(`session-replay.ai-summary-no-events`)}</p>
      </div>
    );
  } else {
    content = (
      <div className={styles.aiSummaryData}>
        <div className={styles.aiSummaryMain}>
          <p className={styles.aiSummaryText}>{aiSummary.summary}</p>
          {aiSummary.key_actions.length > 0 && (
            <div className={styles.aiSummaryActions}>
              <span className={styles.aiSummaryActionsLabel}>{t(`session-replay.key-actions`)}:</span>
              <ul className={styles.aiSummaryActionsList}>
                {aiSummary.key_actions.map((action, index) => (
                  <li key={index} className={styles.aiSummaryAction}>
                    <span className={styles.aiSummaryActionTimestamp} onClick={() => onKeyActionTimestampClick(action)}>
                      {`[${formatRecordingProgressTime(new Date(action.timestamp - replay.time * 1000), TimeDisplaySetting.Seconds)}] `}
                    </span>
                    <br />
                    <span className={styles.aiSummaryActionDescription}>{action.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.aiSummaryPanel}>
      <div className={styles.aiSummaryPanelHeader}>
        <div className={styles.aiSummaryHeaderLeft}>
          <SvgIconReact icon='spark' className={styles.aiSummarySparkIcon} />
          <span className={styles.aiSummaryTitle}>{t(`session-replay.ai-summary-title`)}</span>
        </div>
        {onClose && (
          <ButtonReact
            theme={ButtonTheme.Tertiary}
            icon='x'
            onClick={onClose}
            className={styles.aiSummaryCloseButton}
          />
        )}
      </div>
      <div className={styles.aiSummaryContent}>{content}</div>
    </div>
  );
}
